import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DataTable,
  HairlineCard,
  Icons,
  iconSize,
  ListItemIcon,
  Menu,
  MenuItem,
  RowAction,
  RowActions,
  palette,
  Stack,
  StatusChip,
  statusTone,
  Tooltip,
  Typography,
  useRowActionsColumn,
  useSnackbar,
} from '@munifocus/ui';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocketEntry, ProceedingsStatus } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { CaseManagementDialog } from '../components/CaseManagementDialog';
import { downloadDocket, useDocket, type DocketFormat } from '../lib/proceedings-api';
import { apiErrorMessage } from '../lib/mutation-toast';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * The docket table needs a programmatic name (WCAG 1.3.1), but the heading
 * above it already carries it visually — so the `<caption>` is hidden from
 * sight while staying in the accessibility tree.
 */
const VISUALLY_HIDDEN_CAPTION: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

type ChipColor = 'default' | 'info' | 'success' | 'warning';

/** What a cell renderer is handed: the row plus the locale-aware formatters. */
interface CellContext {
  fmtDate: (v: string | null) => string;
  /** Translate a plea code to its label; `—` when no plea is recorded. */
  fmtPlea: (v: DocketEntry['plea']) => string;
  /** The defendant, or the reader's own words for an unknown owner. */
  fmtDefendant: (v: DocketEntry['defendant']) => string;
}

/** Date columns sort on the stored ISO value — a formatted "3 Aug" sorts as text. */
const DATE_KEYS = new Set(['filedAt', 'firstAppearance', 'returnableDate']);

/**
 * The court-docket table (brief §9.2, 12 columns). These ten render as plain
 * text; the remaining two — Status (a chip) and Notes — are rendered inline.
 */
const COLUMNS: {
  key: string;
  cell: (row: DocketEntry, ctx: CellContext) => string;
}[] = [
  { key: 'reference', cell: (r) => r.reference },
  { key: 'defendant', cell: (r, c) => c.fmtDefendant(r.defendant) },
  { key: 'violation', cell: (r) => r.violation },
  { key: 'statute', cell: (r) => r.statute },
  { key: 'filedAt', cell: (r, c) => c.fmtDate(r.filedAt) },
  { key: 'courtFileNumber', cell: (r) => r.courtFileNumber },
  { key: 'firstAppearance', cell: (r, c) => c.fmtDate(r.firstAppearance) },
  { key: 'returnableDate', cell: (r, c) => c.fmtDate(r.returnableDate) },
  { key: 'plea', cell: (r, c) => c.fmtPlea(r.plea) },
  { key: 'fineImposed', cell: (r) => r.fineImposed ?? '—' },
];

export function CourtDocket() {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const actionsColumn = useRowActionsColumn();
  const { data: docket = [], isLoading, isError } = useDocket();
  const [downloading, setDownloading] = useState<DocketFormat | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  // The docket row currently open in the case-management dialog (P3).
  const [managing, setManaging] = useState<DocketEntry | null>(null);

  const cellContext: CellContext = {
    // Court dates are date-only values stored at UTC midnight; format in UTC so
    // they don't slip to the previous day for viewers in negative timezones.
    fmtDate: (v) =>
      v
        ? frenchFirstOfMonth(new Date(v).toLocaleDateString(i18n.language, { timeZone: 'UTC' }))
        : '—',
    fmtPlea: (v) => (v ? t(`gov.docket.plea.${v}`) : '—'),
    fmtDefendant: (v) => v ?? t('gov.docket.ownerUnknown'),
  };

  const onExport = async (format: DocketFormat) => {
    setDownloading(format);
    try {
      await downloadDocket(format, i18n.language);
    } catch (err) {
      // A download that fails leaves nothing on screen to say so.
      snackbar.error(apiErrorMessage(err, t('gov.docket.exportError')));
    } finally {
      setDownloading(null);
    }
  };

  // Nothing to export from an empty docket — and two greyed-out buttons in the
  // topbar read as broken rather than as "not yet". They appear with the first
  // row.
  // One Export button with a menu rather than two labelled buttons: in French,
  // beside the title and the bar's own controls, the pair pushed the avatar off
  // the edge on a narrow desktop.
  const exportActions = docket.length > 0 && (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Icons.Download size={iconSize.md} />}
        endIcon={<Icons.ChevronDown size={iconSize.sm} />}
        disabled={downloading !== null}
        aria-haspopup="menu"
        aria-expanded={Boolean(exportAnchor)}
        onClick={(e) => setExportAnchor(e.currentTarget)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {downloading ? t('gov.docket.exporting') : t('gov.docket.export')}
      </Button>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setExportAnchor(null);
            void onExport('xlsx');
          }}
        >
          <ListItemIcon>
            <Icons.FileSpreadsheet size={iconSize.md} />
          </ListItemIcon>
          {t('gov.docket.exportExcel')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setExportAnchor(null);
            void onExport('docx');
          }}
        >
          <ListItemIcon>
            <Icons.FileText size={iconSize.md} />
          </ListItemIcon>
          {t('gov.docket.exportWord')}
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <AppShell actions={exportActions || undefined}>
      <PageIntro>{t('gov.docket.subtitle')}</PageIntro>
      <Stack spacing={2}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity="error">{t('gov.docket.loadError')}</Alert>}

        {!isLoading && !isError && docket.length === 0 && (
          <HairlineCard>
            <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center', py: 4, px: 2 }}>
              <Box
                aria-hidden
                sx={{
                  width: 56,
                  height: 56,
                  mb: 1,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: palette.brand.accentTint,
                  color: palette.brand.accent,
                }}
              >
                <Icons.Gavel size={iconSize.xl} strokeWidth={1.75} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {t('gov.docket.empty')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '52ch' }}>
                {t('gov.docket.emptyHint')}
              </Typography>
            </Stack>
          </HairlineCard>
        )}

        {!isLoading && !isError && docket.length > 0 && (
          <DataTable
            rows={docket}
            getRowKey={(row) => row.id}
            minWidth={1180}
            label={t('gov.docket.title')}
            pageSize={25}
            initialSortKey="filedAt"
            initialSortDir="desc"
            emptyLabel={t('gov.docket.empty')}
            search={{
              placeholder: t('gov.docket.searchPlaceholder'),
              of: (row) =>
                `${row.reference} ${cellContext.fmtDefendant(row.defendant)} ${row.violation} ${row.courtFileNumber}`,
            }}
            columns={[
              ...COLUMNS.map((column, index) => ({
                key: column.key,
                label: t(`gov.docket.col.${column.key}`),
                nowrap: true,
                primary: index === 0,
                optional: [
                  'violation',
                  'statute',
                  'returnableDate',
                  'plea',
                  'fineImposed',
                ].includes(column.key),
                mono: column.key === 'reference' || column.key === 'courtFileNumber',
                sortValue: (row: DocketEntry) =>
                  DATE_KEYS.has(column.key)
                    ? (((row as unknown as Record<string, string | null>)[column.key] ??
                        '') as string)
                    : column.cell(row, cellContext),
                cell: (row: DocketEntry) => column.cell(row, cellContext),
              })),
              {
                key: 'status',
                label: t('gov.docket.col.status'),
                sortValue: (row: DocketEntry) => row.status,
                cell: (row: DocketEntry) => (
                  <StatusChip
                    status={statusTone.docket[row.status]}
                    label={t(`gov.docket.status.${row.status}`)}
                  />
                ),
              },
              {
                key: 'notes',
                label: t('gov.docket.col.notes'),
                optional: true,
                sortValue: (row: DocketEntry) => row.notes ?? '',
                cell: (row: DocketEntry) =>
                  row.notes ? (
                    // Clamped to two lines: one long note was making its row
                    // three times the height of its neighbours.
                    <Tooltip title={row.notes}>
                      <Box
                        sx={{
                          maxWidth: 240,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {row.notes}
                      </Box>
                    </Tooltip>
                  ) : (
                    '—'
                  ),
              },
              {
                ...actionsColumn,
                cell: (row: DocketEntry) => (
                  <RowActions>
                    <RowAction
                      label={t('gov.docket.manage.action')}
                      icon={Icons.Pencil}
                      onClick={() => setManaging(row)}
                    />
                  </RowActions>
                ),
              },
            ]}
          />
        )}

        {managing && (
          <CaseManagementDialog entry={managing} open onClose={() => setManaging(null)} />
        )}
      </Stack>
    </AppShell>
  );
}
